import { amorphicStatic, property, remote, supertypeClass } from '@haventech/amorphic';

import { Address } from './Address';
import { Applicant } from './Applicant';
import { Application } from './Application';
import { Assumptions } from '../static/Assumptions';
import { Auth0Client, Auth0ResponseAccount } from '../authentication/Auth0Client';
import { Auth0Utils } from '../authentication/Auth0Utils';
import { AuthenticatedPrincipal } from '../AuthenticatedPrincipal';
import { CapitalNeeds } from '../needs/CapitalNeeds';
import { EmailLead } from './EmailLead';
import { Holidays } from '../static/Holidays';
import { MetadataHierarchy } from '../metadata/MetadataHierarchy';
import { Party } from './Party';
import { Phone } from './Phone';
import { Policy } from './Policy';
import { Product } from '../static/Product';
import { Profile } from '../needs/Profile';
import { Quotes } from '../needs/Quotes';
import { Utils } from '../Utils';
import { Settings } from './Settings';
import { Visitor } from './Visitor';
import { DocuSign } from '../docusign/DocuSign';
import { ApplicationTypes } from '../../../coverpath-common/js/interfaces/CoverpathTypes';
import { CommonUtilsBackend } from '../static/CommonUtilsBackend';
import { LoggerSingleton, Category } from '../logger/LoggerSingleton';
import { UICategory, UILoggerSingleton, UIPiiLevelEnum } from '../logger/UILoggerSingleton';

export const enum AuthOwner {
  HAVEN = 'HAVEN',
  AUTH0 = 'AUTH0',
}

export interface OverTime {
  [key: number]: {
    label: string;
    data: number[];
  };
}

export interface QuoteOptions {
  face: number;
  overTime: OverTime[];
  term: number;
  scenarioFace: number;
  scenarioTerm: number;
  applicant: Applicant;
  state: string;
  product: {
    quoteKey: string;
    waiverKey: string;
  };
}

export interface SelectedQuote {
  policies: {
    face: number;
    term: number;
    monthly: number;
    carrierCode: string;
    carrier: string;
    waiverOfPremium: boolean;
    product: string;
    rating: string;
  }[];
}

@supertypeClass({ toClient: true, toServer: true })
export class Customer extends AuthenticatedPrincipal {
  //TODO
  static createExternalCustomer = function () {};

  setDateOfBirth(dateOfBirth: string) {
    try {
      this.primaryCustomer.person.dob = new Date(dateOfBirth);
      this.primaryCustomer.person.dobTrigger();
    } catch (err) {
      UILoggerSingleton.getInstance().error(
        {
          module: 'Customer',
          function: 'setDateOfBirth',
          category: UICategory.request,
          error: {
            name: err?.name,
            message: err?.message,
            stack: err?.stack,
            isHumanRelated: false,
          },
        },
        'Date of birth is not a valid date.'
      );
    }
  }

  @property({
    toServer: false,
    comment: `Channel through which the application is being submitted. Domain of Values are ${Object.keys(
      Product.channels
    )}`,
  })
  channel: string = 'CAS';

  @property({ toServer: false })
  language: string = 'English';

  @property({
    toServer: false,
    length: 50,
    rule: ['email'],
    logChanges: true,
    sensitiveData: true,
    comment: `Login ID using which the client logs in. This is the owner's email ID`,
  })
  email: string;

  @property({ toServer: false, length: 50, rule: ['email'] })
  newEmail: string;

  @property({
    toServer: false,
    length: 40,
    rule: ['name', 'required'],
    comment: `First name of Customer. Populated for CAS customers as of now`,
  })
  firstName: string;

  @property({
    toServer: false,
    length: 40,
    rule: ['name', 'required'],
    comment: `Last name of Customer. Populated for CAS customers as of now`,
  })
  lastName: string;

  @property({ isLocal: true })
  assumptions;

  /* Probably should hang off of the controller*/
  @property({ type: Array, applicationOmit: true, getType: () => Party })
  persons: Array<Party> = [];

  @property({ type: Array, applicationOmit: true, getType: () => Address })
  addresses: Array<Address> = [];

  @property({ type: Array, applicationOmit: true, getType: () => Phone })
  phones: Array<Phone> = [];

  @property({ isLocal: true }) // local property for multiple policies
  maxAllowedFace: number | null = null;

  @property({ isLocal: true }) // local property for multiple policies
  maxAllowedTerm: number | null = null;

  @property()
  selectedQuote;

  // TODO JOINT APP - progress bound to customer -> should move to application?
  @property({
    logChanges: true,
    values: {
      '1': 'Needs',
      '2': 'Quotes listed',
      '10': 'Introduction screen',
      '11': 'Account registered',
      '12': 'Apply form',
      '13': 'Application completed',
    },
  })
  progress: number = 1;

  /* The two individuals on whom we do needs analysis*/
  @property({ persist: false, getType: () => Applicant })
  _primaryCustomer: Applicant;

  @property()
  alternateCustomer: Applicant;

  @property({ type: Array, toServer: false, getType: () => Policy })
  policies: Array<Policy> = [];

  @property({ type: Array, getType: () => Visitor })
  visitors: Array<Visitor> = [];

  @property({ toServer: false, getType: () => Application, comment: 'All the applications for a given customer' })
  protected applications: Array<Application> = [];

  @property({
    comment: 'timestamp',
    toServer: false,
  })
  createdTime: Date;

  @property({ toServer: true, toClient: true, persist: false, getType: () => Application })
  protected _currentApplication: Application;

  /**
   * Get the active application for this customer. For Haven, there should be only one in activeApplications array.
   * An active application has type as 'active'.
   * A non-active application could have its type populated as 'prior' or 'deleted' etc.
   */
  get activeApplications() {
    const activeApplications: Array<Application> = this.applications;
    if (activeApplications.length > 1) {
      const applicationNumbers: Array<string> = [];
      activeApplications.forEach((application) => {
        applicationNumbers.push(application.applicationNumber);
      });
      UILoggerSingleton.getInstance().error(
        {
          module: 'Customer',
          function: 'activeApplications',
          category: UICategory.request,

          error: {
            isHumanRelated: false,
          },
        },
        `There should be only one active Haven application, but found ${
          activeApplications.length
        }:${applicationNumbers.join(', ')}
                for customer id ${this._id}.`
      );
    }

    return activeApplications;
  }

  get application(): Application {
    return this.getApplication();
  }

  set application(application: Application) {
    this.setApplication(application);
  }

  getApplication(): Application {
    return this._currentApplication || this.activeApplications[0];
  }

  setApplication(application: Application) {
    this._currentApplication = application;
    if (!this.applications.includes(application)) {
      this.applications.unshift(application);
      this.applications = this.applications.filter((app) => app.type === ApplicationTypes.ACTIVE);
    }
  }

  get primaryCustomer(): Applicant {
    // if primaryPolicy is already created, then we can just get the primaryCustomer from policy.insured.
    if (this.application && this.application.primaryPolicy) {
      return this.application.primaryPolicy.insured;
    }
    return this._primaryCustomer;
  }

  set primaryCustomer(applicant: Applicant) {
    // if primaryPolicy is already created, then we can just get the primaryCustomer from policy.insured
    // therefore no need to set _primaryCustomer.
    if (this.application && this.application.primaryPolicy) {
      return;
    }
    this._primaryCustomer = applicant;
  }

  @property({ toServer: false, toClient: ['admin'], getType: () => Application })
  priorApplications: Array<Application> = [];

  @property({ toServer: false })
  reapplyTokensLeft: number = 0;

  @property({ toServer: false })
  reapplyTokensUsed: number = 0;

  @property({ isLocal: true })
  hlQuotes = {};

  @property()
  percentFilled: number;

  /* Marketing Metrics*/
  @property()
  lastPageVisited: string = 'home';

  @property()
  variations: string;

  @property()
  referrers: string;

  @property()
  primaryReferrer: string;

  @property()
  metrics = {};

  @property({ toServer: false })
  partnerName: string;

  @property({ toServer: false })
  originalReferrer: string;

  @property({ toServer: false })
  currentReferrer: string;

  @property({ toServer: false })
  timeOfFirstVisit: number;

  //TODO: To be removed (https://massmutual.atlassian.net/browse/HL-5525)
  @property({ toServer: false, logChanges: true })
  visitorId: string;

  @property()
  paidSource: string;

  @property()
  paidSource_cid: string;

  @property()
  paidSource_pdata: string;

  @property()
  paidSource_campaign: string;

  @property()
  mobileAppSawPopup: boolean;

  @property()
  click2callSawPopup: boolean;

  @property({ isLocal: true })
  click2callFirstTime: boolean = true;

  @property({ isLocal: true })
  click2callAction: string;

  @property({ isLocal: true, rule: ['required', 'telephone'] })
  click2callPhone: string;

  @property({ isLocal: true })
  click2callName: string;

  @property({
    comment: `Stores the date and time of user's response when interacting with the survey banner`,
  })
  lastSurveyResponseDate: Date;

  @property({
    isLocal: true,
    rule: ['required'],
    values: function () {
      const values = [];
      let d = new Date();
      const hoursBehindET = (d.getTimezoneOffset() - 240) / 60;

      const businessHours = {
        start: 9 - hoursBehindET,
        end: 18 - hoursBehindET,
      };

      const isWorkday = function (d) {
        return (
          !Utils.isInWeekend(d) &&
          Holidays.map(function (obj) {
            return obj.date;
          }).indexOf(d.toISOString().substring(0, 10)) === -1
        );
      };

      const time = (d.getHours() + hoursBehindET) * 100 + d.getMinutes();

      if (isWorkday(d) && time < (businessHours.end - 1) * 100 + 30) {
        values.push(d.toISOString().slice(0, 10));
      }

      for (let i = 1; i < 8; i++) {
        d = new Date();
        d.setDate(d.getDate() + i);

        if (isWorkday(d)) {
          values.push(d.toISOString().slice(0, 10));
        }
      }

      return values;
    },
    descriptions: function () {
      const obj = {};
      const months = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ];
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

      for (let i = -1; i <= 8; i++) {
        const d = new Date();

        d.setDate(d.getDate() + i);

        let dateStr = days[d.getDay()] + ', ' + months[d.getMonth()] + ' ' + d.getDate();

        dateStr += (i === 0 ? ' (Today)' : '') + (i === 1 ? ' (Tomorrow)' : '');
        obj[d.toISOString().slice(0, 10)] = dateStr;
      }

      return obj;
    },
  })
  click2callDate: string;

  @property({
    isLocal: true,
    rule: ['required'],
    values: function () {
      const values = [
        '900',
        '930',
        '1000',
        '1030',
        '1100',
        '1130',
        '1200',
        '1230',
        '1300',
        '1330',
        '1400',
        '1430',
        '1500',
        '1530',
        '1600',
        '1630',
        '1700',
        '1730',
      ];
      const d = new Date();

      if (this.click2callDate === d.toISOString().slice(0, 10)) {
        const hoursBehindET = (d.getTimezoneOffset() - 240) / 60;

        d.setMinutes(d.getMinutes() + 15);

        const minCallTime = (d.getHours() + hoursBehindET) * 100 + d.getMinutes();

        while (values.length > 1 && Number(values[0]) < minCallTime) {
          values.shift();
        }
      }

      return values;
    },
    descriptions: {
      '900': '9:00 AM',
      '930': '9:30 AM',
      '1000': '10:00 AM',
      '1030': '10:30 AM',
      '1100': '11:00 AM',
      '1130': '11:30 AM',
      '1200': '12:00 PM',
      '1230': '12:30 PM',
      '1300': '1:00 PM',
      '1330': '1:30 PM',
      '1400': '2:00 PM',
      '1430': '2:30 PM',
      '1500': '3:00 PM',
      '1530': '3:30 PM',
      '1600': '4:00 PM',
      '1630': '4:30 PM',
      '1700': '5:00 PM',
      '1730': '5:30 PM',
    },
  })
  click2callTime: string;

  /* Obsolete*/
  @property()
  applyingFor: string = 'both';

  @property()
  termQuoteMode: string = 'all';

  @property()
  customerSelection: number;

  /* Local data never persisted*/
  @property({ persist: false })
  quoteEngine: Quotes;

  @property({ comment: "Applicant's information of the capital needs analysis for estimating life insurance coverage" })
  capitalNeeds: CapitalNeeds;

  @property({ isLocal: true })
  ratingSelection: number;

  @property({ isLocal: true })
  paymentPerformanceSelection: number;

  @property({ isLocal: true })
  complaintSelection: number;

  @property({ isLocal: true })
  complaintThresholds;

  @property({ isLocal: true })
  paymentPerformanceThresholds;

  @property({ isLocal: true })
  brandSelection;

  @property({
    getType: () => Profile,
    comment: "Applicant's background information(marriage staus, number of kids, house owner, education, etc.)",
  })
  profile: Profile;

  @property({ applicationOmit: true, comment: 'Haven settings for expected retirement age, inflation rate, etc' })
  settings: Settings;

  @property({ isLocal: true })
  isValidState;

  @property({
    getType: () => EmailLead,
    comment:
      'Email address signed up for the online insurance application or email lead from marketing channels(news letter, blog, contact)',
  })
  emailLead: EmailLead;

  @property({ comment: 'Page Scoped User ID that serves as a reference to a Facebook Messenger chat session' })
  facebookFormSessionID: string;

  // this is only used in the scope of a single request
  // this is never sent to the browser, and never cached in Redis, and never persisted in the DB
  private postServerCallCallbacks: Array<() => any>;

  @property({ isLocal: true })
  prefilledCustomerEmail: string;

  constructor() {
    super();
  }

  static new(channel?: string) {
    return new Customer().init(channel);
  }

  init(channel?: string) {
    super.init();

    this.channel = channel;

    this.reset();

    return this;
  }

  async notifyPostServerCallCallbacks() {
    if (this.postServerCallCallbacks) {
      await Promise.all(this.postServerCallCallbacks.map((cb) => cb()));
      this.clearPostServerCallCallbacks();
    }
  }

  clearPostServerCallCallbacks() {
    this.postServerCallCallbacks = undefined;
  }

  registerPostServerCallCallback(cb: () => any) {
    this.postServerCallCallbacks = this.postServerCallCallbacks || [];
    this.postServerCallCallbacks.push(cb);
  }

  setLocationByZip(zip: string): Promise<void> {
    return CommonUtilsBackend.publicGetLocationByZip(zip).then((location) => {
      if (!location) {
        this.primaryCustomer.address.zip = zip;
        this.primaryCustomer.address.state = '';
        this.primaryCustomer.address.city = '';
      } else {
        this.primaryCustomer.address.zip = zip;
        this.primaryCustomer.address.state = location.stateCode;
        this.primaryCustomer.address.city = location.city;
        this.primaryCustomer.address.latitude = location.lat;
        this.primaryCustomer.address.longitude = location.lng;
        this.primaryCustomer.address.timezone = location.timezone;
      }
    });
  }

  getLanguageCode() {
    return Assumptions.languageCodes[this.language];
  }

  getChannelInfo() {
    return Product.channels[this.channel];
  }

  getRefundDays() {
    return Assumptions.refundDays;
  }

  /* Most recently selected quote*/
  setSelectedQuote(selectedQuote?) {
    // setter to allow subclasses to override / extend
    this.selectedQuote = selectedQuote;
  }

  advanceApplicationProgress(nextState) {
    if (this.progress <= nextState) {
      this.progress = nextState;
    }
  }

  resetApplicationProgress(resetState) {
    if (this.application && this.application.primaryPolicy) {
      this.progress = resetState;
    }
  }

  addReapplyToken() {
    this.reapplyTokensLeft++;

    return this.persistSave();
  }

  removeReapplyToken() {
    if (this.reapplyTokensLeft > 0) {
      this.reapplyTokensLeft--;

      return this.persistSave();
    }
  }

  applyTokenUsed() {
    if (this.reapplyTokensLeft > 0) {
      this.reapplyTokensLeft--;
      this.reapplyTokensUsed++;
    }
  }

  setReapplyNegative() {
    this.reapplyTokensLeft = -1;
    return this.persistSave();
  }

  setPaidSource(url?: string) {
    const utm_source = Utils.getParameterByName('utm_source', url);

    if (utm_source || url) {
      this.paidSource = utm_source;
      this.paidSource_cid = Utils.getParameterByName('cid', url) || '';
      this.paidSource_pdata = Utils.getParameterByName('pdata', url) || '';
      this.paidSource_campaign = Utils.getParameterByName('utm_campaign', url) || '';
    }
    UILoggerSingleton.getInstance().info(
      {
        module: 'Customer',
        function: 'setPaidSource',
        category: UICategory.request,
        data: {
          url: url,
          partnerName: this.partnerName,
          utm_source: utm_source,
        },
      },
      `Setting paidSource for customer id ${this.__id__.valueOf()}`
    );
  }

  submitClick2CallNow() {
    const name = this.click2callName;
    const phone = Utils.formatTelephone(this.click2callPhone);

    window['$zopim'](function () {
      const isChatting = window['$zopim'].livechat.isChatting();

      window['$zopim'].livechat.setName(name || 'user');
      window['$zopim'].livechat.addTags('outreach');

      //to block the auto-response trigger
      window['$zopim'].livechat.say('Call requested at ' + phone);

      if (!isChatting) {
        window['$zopim'].livechat.hideAll();
      }
    });
  }

  clientInit() {
    this.assumptions = Assumptions;

    let year;

    for (year = 2014; year < 2150; ++year) {
      this.assumptions.ssnTable[year] = [this.assumptions.ssnTable[year - 1][0] * (1 + this.assumptions.ssnCOLA), 1];
    }

    for (year = 2015; year < 2150; ++year) {
      this.assumptions.ssnMaxIncome[year] = this.assumptions.ssnMaxIncome[year - 1] * (1 + this.assumptions.ssnCOLA);
    }

    if (this.primaryCustomer) {
      this.primaryCustomer.clientInit();
    }

    if (this.alternateCustomer) {
      this.alternateCustomer.clientInit();
    }

    if (!this.quoteEngine) {
      this.quoteEngine = Quotes.new();
    }
  }

  reset() {
    this.clientInit();
    this.persons = [];
    this.addresses = [];
    this.phones = [];
    this.primaryCustomer = Applicant.new(this);
    this.alternateCustomer = Applicant.new(this);
    this.alternateCustomer.addressSameAsPrimary = true;
  }

  /**
   * Should be overridden in derived classes
   * @param {Object} _scenario - not sure
   * @returns {{face: *, overTime: *, term: *, applicant: *, state: *, scnearioFace: *, scenarioTerm: *}}
   */
  getQuoteOptions(_scenario?): any {
    return {};
  }

  /**
   * Protected function for enrichCustomerOptimized to set the _currentApplication.
   * For Haven, it is always the first and only active application.
   */
  protected setCurrentApplication(applicationId?: string) {
    this._currentApplication = this.activeApplications[0];
  }

  enrichCustomerOptimized(applicationId?: string) {
    const idMap = {};

    idMap[this._id] = this;

    const retrievePersons = () => {
      UILoggerSingleton.getInstance().info(
        {
          module: 'Customer',
          function: 'enrichCustomerOptimized',

          category: UICategory.request,
        },
        `retrievePersons`
      );

      return this.fetch(this.retrievePersonSpec(), null, idMap);
    };

    const retrieveApplicants = () => {
      UILoggerSingleton.getInstance().info(
        {
          module: 'Customer',
          function: 'enrichCustomerOptimized',
          category: UICategory.request,
        },
        `retrieveApplicants`
      );

      return this.fetch(this.retrieveApplicantsSpec(), null, idMap);
    };

    const retrieveApplication = () => {
      UILoggerSingleton.getInstance().info(
        {
          module: 'Customer',
          function: 'enrichCustomerOptimized',
          category: UICategory.request,
        },
        `retrieveApplication`
      );

      return this.fetch(this.retrieveApplicationSpec(), null, idMap);
    };

    const retrieveNeeds = () => {
      const spec = this.retrieveNeedsSpec();

      if (spec) {
        UILoggerSingleton.getInstance().info(
          {
            module: 'Customer',
            function: 'enrichCustomerOptimized',
            category: UICategory.request,
            context: { piiLevel: UIPiiLevelEnum.s2 },
            data: {
              spec: spec,
            },
          },
          `retrieveNeeds`
        );

        return this.fetch(spec, null, idMap);
      }
    };

    const retrieveEmailLead = () => {
      const spec = this.retrieveEmailLeadSpec();

      if (spec) {
        UILoggerSingleton.getInstance().info(
          {
            module: 'Customer',
            function: 'enrichCustomerOptimized',
            category: UICategory.request,
            data: {
              spec: spec,
            },
          },
          `retrieveEmailLead`
        );

        return this.fetch(spec, null, idMap);
      }
    };

    const setupPolicies = () => {
      UILoggerSingleton.getInstance().info(
        {
          module: 'Customer',
          function: 'enrichCustomerOptimized',
          category: UICategory.request,
        },
        `setupPolicies`
      );

      // Ensure we don't make things dirty which could cause WF update conflicts
      this.amorphic.withoutChangeTracking(
        function () {
          this.policies = [];

          if (this.application && this.application.primaryPolicy) {
            this.application.primaryPolicy.application = this.application;
            this.policies.push(this.application.primaryPolicy);
          }
        }.bind(this)
      );

      return true;
    };

    return Promise.resolve()
      .then(retrievePersons)
      .then(retrieveApplicants)
      .then(retrieveApplication)
      .then(retrieveNeeds)
      .then(retrieveEmailLead)
      .then(() => {
        this.setCurrentApplication(applicationId);
      })
      .then(() => {
        this.retrievePersonalHistory();
      })
      .then(setupPolicies);
  }

  retrievePersonalHistory() {
    const idMap = {};

    idMap[this._id] = this;

    // the DB can't handle retrieving all the personal history props
    // in one fetch, so broke this group into 3
    const specSection1 = this.retrievePersonalHistorySection1();
    const specSection2 = this.retrievePersonalHistorySection2();
    const specSection3 = this.retrievePersonalHistorySection3();

    UILoggerSingleton.getInstance().info(
      {
        module: 'Customer',
        function: 'retrievePersonalHistory',
        category: UICategory.request,
      },
      `retrievePersonalHistory`
    );

    return Promise.all([
      this.primaryCustomer.fetch(specSection1, null, idMap),
      this.primaryCustomer.fetch(specSection2, null, idMap),
      this.primaryCustomer.fetch(specSection3, null, idMap),
    ]);
  }

  retrievePersonSpec() {
    return {
      settings: true,
      profile: true,
    };
  }

  retrieveApplicantsSpec() {
    return {
      alternateCustomer: {
        fetch: {
          familyHistory: true,
          personalHistory: true,
          riskFactors: true,
        },
      },
    };
  }

  retrieveApplicationSpec() {
    return {
      applications: {
        fetch: {
          policies: {
            fetch: {
              insured: {
                fetch: {
                  familyHistory: { fetch: true },
                  personalHistory: { fetch: true },
                  riskFactors: { fetch: true },
                },
              },
            },
          },
        },
      },
    };
  }

  retrieveNeedsSpec() {
    return {
      capitalNeeds: true,
    };
  }

  retrieveEmailLeadSpec() {
    return {
      emailLead: true,
    };
  }

  retrievePersonalHistoryBDSpec() {
    return {
      hasBDAnemiaResolvedDiagnosis: true,
      hadBDAnemiaBleedingDiagnosis: true,
      hasBDAnemiaControlledDiagnosis: true,
      hasBDAnemiaSymptomsDiagnosis: true,
      hadBDSpleenDiagnosis: true,
      hadBDClottingDiagnosis: true,
      hadBDAneurysmDiagnosis: true,
      hadBDAutismDetails: true,
      hadBDAnemiaDiagnosis: true,
      hadBDHemochromatosisDiagnosis: true,
      hadMGUSDiagnosis: true,
      hadMyelemoDiagnosis: true,
      hadBloodClotOrCoagulationDefectsDiagnosis: true,
    };
  }

  retrievePersonalHistoryBDMSpec() {
    return {
      hadBMDAmputationsFromTraumaDiagnosis: true,
      hadBMDATypeDiagnosis: true,
      hasBMDMedsDiagnosis: true,
      hasBMDDisabilityBenefitsDiagnosis: true,
      hasBMDOsteoporosisDiagnosis: true,
      hasBMDPagetsDiagnosis: true,
      diagosisBMDAOther: true,
      hasBMDBackPainMedsDiagnosis: true,
    };
  }

  retrievePersonalHistoryCncrSpec() {
    return {
      hadCncrBreastDiagnosis: true,
      hadCncrBrainDiagnosis: true,
      hadCncrColonDiagnosis: true,
      hadCncrOvarianDiagnosis: true,
      hadCncrProstateDiagnosis: true,
      hadCncrRenalDiagnosis: true,
      hadCancerDiagnosis: true,
      hadSkinCncrMelanomaDiagnosis: true,
      hadSkinCncrOtherDiagnosis: true,
      hadLeukemiaDiagnosis: true,
      hadLymphomaDiagnosis: true,
      hadCncrTesticularDiagnosis: true,
    };
  }

  retrievePersonalHistoryCPSpec() {
    return {
      CPDetails: true,
      hadCPPreclampsiaDiagnosis: true,
      hadAbnormalUterineBleedingDiagnosis: true,
      hadCPHELLPDiagnosis: true,
      hadCPHeartLiverKidneyProblemsDiagnosis: true,
      hadCPSeizuresDiagnosis: true,
      hadCPGestationalDiabitiesDiagnosis: true,
    };
  }

  retrievePersonalHistoryDBSpec() {
    return {
      hadDBCerebralPalsyDetails: true,
      hadDBDownSyndromeDetails: true,
      hadDBTumorDiagnosis: true,
      hadDBEncephalititsDiagnosis: true,
      hadDBSeizuresDiagnosis: true,
      hadDBMDisabilityPaymentsDiagnosis: true,
      hadDBMSDiagnosis: true,
      hadDBNeuropathyDiagnosis: true,
      hadDBParkinsonsDiagnosis: true,
      hadDBStrokeDiagnosis: true,
      hadDBFMultipleEpisodesDiagnosis: true,
      hadDBFLastYearDiagnosis: true,
      hadDBTIADiagnosis: true,
      hadDBHematomaDiagnosis: true,
      DiagnosisisDTNotSure: true,
      diabetesDiagnosis: true,
    };
  }

  retrievePersonalHistoryDDSpec() {
    return {
      hadDDCeliacDiseaseDiagnosis: true,
      hadDDColonPolypDiagnosis: true,
      hadDDChronsDiagnosis: true,
      hadDDColitusNonUlcerativeDiagnosis: true,
      hadDDColitusUlcerativeDiagnosis: true,
      hadDDDLast6MonthsDiagnosis: true,
      hadDDNAFLDDiagnosis: true,
      hadDDHepititisDiagnosis: true,
      hadDDPancreaticInflamationDiagnosis: true,
      hadDDPancreatitisDiagnosis: true,
      hadDDURecoveredDiagnosis: true,
      hadDDWeightLossSurgeryDiagnosis: true,
      hadDDAnorectalFistulaDiagnosis: true,
      hadDDGastritisDiagnosis: true,
      hadDDBenignDiagnosis: true,
      diagnosisDDOther: true,
    };
  }

  retrievePersonalHistoryECGSpec() {
    return {
      hadECGNormalDiagnosis: true,
    };
  }

  retrievePersonalHistoryEENTSpec() {
    return {
      hadEENTAcousicNeuromaDiagnosis: true,
      hadEENTLeukoplakiaDiagnosis: true,
      hadEENTOpticNeuritisDiagnosis: true,
    };
  }

  retrievePersonalHistoryEOSpec() {
    return {
      hadEOHospitalizedLast5YearsDiagnosis: true,
      hadEOAlcoholismDiagnosis: true,
      hadEOADepressionDiagnosis: true,
      hadEODiagnosis: true,
      hadEODelusionDiagnosis: true,
      hadEODrugCounselingDiagnosis: true,
      hadEOEatingDisorderDiagnosis: true,
      hadEOEatingDisorderHospitalizedDiagnosis: true,
      hadEOMajorDepressionDiagnosis: true,
      hadEOPAMedsLast2YearsDiagnosis: true,
      hadEOSuicideAttemptDiagnosis: true,
      hadChangeDoseEOATreatmentDiagnosis: true,
      hadEOPsychosisHospitalizedDiagnosis: true,
      hadEOADepressionOther: true,
      hadEODepressionSocialSituationalDiagnosis: true,
      hadEODelusionHospitalizedLast10YearsDiagnosis: true,
      hadEOPTSDHospitalizedLast10YearsDiagnosis: true,
      hadEOOCDHospitalizedLast10YearsDiagnosis: true,
      hadEOBipolarHospitalizedDiagnosis: true,
      diagnosisEONotSure: true,
      hadAlcoholAbuseDiagnosis: true,
      hadEOADepressionHospitalizedDiagnosis: true,
    };
  }

  retrievePersonalHistoryHCSpec() {
    return {
      hadHCAttackDiagnosis: true,
      hadHCAbnormalBeatDiagnosis: true,
      hadHCHMedicationDiagnosis: true,
      hadHCMurmurDiagnosis: true,
      hadHCPericarditisDiagnosis: true,
      hadHCAorticAneurysmDiagnosis: true,
      hadHCPeripheralArteryVeinDiseaseDiagnosis: true,
      hadHCChestPainsDiagnosis: true,
    };
  }

  retrievePersonalHistoryIDSpec() {
    return {
      hadIDOBenignDiagnosis: true,
      hadIDPolycysticDiagnosis: true,
      DiagnosisIDNotSure: true,
      hadIDCervicitisDiagnosisDiagnosis: true,
    };
  }

  retrievePersonalHistoryRDSpec() {
    return {
      hadRDCOPDDiagnosis: true,
      hadRDEmphysemaDiagnosis: true,
      hadRDAHospitalizedLast1YearDiagnosis: true,
      hadRDAERLast1YearDiagnosis: true,
      hasRDAMedsDiagnosis: true,
      hadRDPulmonaryNoduleDiagnosis: true,
      hasRDApneaDiagnosis: true,
      hasRDPRecoveredDiagnosis: true,
      hadRDTuberculosisDiagnosis: true,
      hadRDSarcoidosisDiagnosis: true,
      hadRDChronicFatigueDiagnosis: true,
      hadRDEBOtherSymptomsDiagnosis: true,
      hadRDLupusDiagnosis: true,
      hadRDLymeDiseaseDetails: true,
      hasRDBRecoveredDiagnosis: true,
      diagnosisRDOther: true,
      diagnosisRMDOther: true,
      hasHypothyroidDiagnosis: true,
      hasDTProlactinomaDiagnosis: true,
      hasDTTImageBiopsyDiagnosis: true,
      hasDTTStableDiagnosis: true,
    };
  }

  retrievePersonalHistorySDSpec() {
    return {
      DiagnosisSDNotSure: true,
      hadSDArthritisDiagnosis: true,
      hasSDMedsDiagnosis: true,
    };
  }

  retrievePersonalHistoryTBSpec() {
    return {
      hasTBISymptomsOrMedsDiagnosis: true,
      diagnosisTB: true,
    };
  }

  retrievePersonalHistoryUTDSpec() {
    return {
      hadUTDGlomerulonephiritisDiagnosis: true,
      hasUTDNormalKidneyDiagnosis: true,
      hadUTDRenalFailureDiagnosis: true,
      hadUTDProteinuriaDiagnosis: true,
      hadUTDRCMultipleDiagnosis: true,
      hadUTStructuralDiagnosis: true,
      hadUTDElevatedPSADiagnosis: true,
      hadUTDInterstitialCystitisDiagnosis: true,
      DiagnosisUTDNotSure: true,
      hadUrninaryTrackDisorderDiagnosis: true,
    };
  }

  retrievePersonalHistoryNephritisSpec() {
    return {
      hadNephritisLast2YearsDiagnosis: true,
      hadNephritisMoreThanLast2YearsDiagnosis: true,
    };
  }

  retrievePersonalHistoryHIVSpec() {
    return {
      hadHIVTreatmentAdviceDiagnosis: true,
      hivNegativeAddress: true,
      hadImmunoDeficiencyDiagnosis: true,
      hadFullTestExclHIVDiagnosis: true,
      hivPositiveAddressTerm2: false,
    };
  }

  retrievePersonalHistoryMiscSpec() {
    return {
      primaryPhysicianPerson: true,
      hadTreatmentOtherDiagnosis: true,
      hadNotCompletedTestDiagnosis: true,
      disabledDetails: true,
    };
  }

  retrievePersonalHistorySection1() {
    return {
      personalHistory: {
        fetch: {
          ...this.retrievePersonalHistoryBDSpec(),
          ...this.retrievePersonalHistoryBDMSpec(),
          ...this.retrievePersonalHistoryCncrSpec(),
          ...this.retrievePersonalHistoryCPSpec(),
          ...this.retrievePersonalHistoryDBSpec(),
          ...this.retrievePersonalHistoryDDSpec(),
        },
      },
    };
  }

  retrievePersonalHistorySection2() {
    return {
      personalHistory: {
        fetch: {
          ...this.retrievePersonalHistoryECGSpec(),
          ...this.retrievePersonalHistoryEENTSpec(),
          ...this.retrievePersonalHistoryEOSpec(),
          ...this.retrievePersonalHistoryHCSpec(),
          ...this.retrievePersonalHistoryIDSpec(),
        },
      },
    };
  }

  retrievePersonalHistorySection3() {
    return {
      personalHistory: {
        fetch: {
          ...this.retrievePersonalHistoryRDSpec(),
          ...this.retrievePersonalHistorySDSpec(),
          ...this.retrievePersonalHistoryTBSpec(),
          ...this.retrievePersonalHistoryUTDSpec(),
          ...this.retrievePersonalHistoryNephritisSpec(),
          ...this.retrievePersonalHistoryHIVSpec(),
          ...this.retrievePersonalHistoryHIVSpec(),
          ...this.retrievePersonalHistoryMiscSpec(),
        },
      },
    };
  }

  @remote({ on: 'server' })
  cloneApplication(newProgress?) {
    const oldApplication = this.application;
    return cloneApp
      .bind(this)()
      .then(saveChanges.bind(this))
      .then(
        function () {
          return this;
        }.bind(this)
      );

    function cloneApp() {
      this.amorphic.__transient__ = true;
      this.priorApplications.push(oldApplication);

      return Promise.resolve()
        .then(
          function () {
            return this.application.createClonedApplicationAsync();
          }.bind(this)
        )
        .then(
          function (app) {
            // newly cloned app
            oldApplication.type = ApplicationTypes.PRIOR;
            this.application = app;
            this.primaryCustomer = this.application.primaryPolicy.insured;
            if (this.application.secondaryPolicy) {
              this.application.secondaryPolicy.insured = this.application.primaryPolicy.insured;
            }

            if (typeof newProgress !== 'undefined') {
              this.resetApplicationProgress(newProgress);
            }

            this.amorphic.__transient__ = false;

            return this.application.assignApplicationNumber();
          }.bind(this)
        );
    }

    function saveChanges() {
      const txn = this.amorphic.begin(true);

      oldApplication.setDirty(txn);

      this.application.setDirty(txn, false, true);
      this.setDirty(txn, false, true);

      return this.amorphic.end(txn);
    }
  }

  public resetApplication(sourceApplication: Application, newProgress, newPolicy?: Policy) {
    this.priorApplications.push(sourceApplication);

    if (typeof newProgress !== 'undefined') {
      this.resetApplicationProgress(newProgress);
    }

    const selectedQuote: SelectedQuote = this.application.primaryPolicy.selectedQuote;

    // This is Haven only
    return this.switchPolicy(selectedQuote, newPolicy);
  }

  /**
   * Switch to a new application and policy, or a bare-bones one if none is passed.
   * Use the passed selectedQuote, or default to `this.SelectedQuote`
   *
   * @remarks CP and MMD override this implementation
   */
  @remote({ on: 'server' })
  async switchPolicy(selectedQuote?, policyToSwitchTo?: Policy): Promise<void> {
    if (!selectedQuote) {
      selectedQuote = this.selectedQuote;
    }

    const primaryAddress = this.primaryCustomer.address;
    const product = this.application.primaryPolicy.product;

    return Promise.resolve().then(markPriorApp.bind(this)).then(createApp.bind(this)).then(switchPol.bind(this));

    function markPriorApp() {
      // use a direct reference of the application so persistSave is called on the application itself.
      // if using this.application, only an active application will be returned and it will be undefined in this case
      // because the same application was just marked as prior.
      const application = this.application;
      application.type = ApplicationTypes.PRIOR;
      return application.persistSave();
    }

    async function createApp() {
      this.application = Application.new(this);
      await this.application.assignApplicationNumber();
      return this.application.persistSave();
    }

    function switchPol() {
      if (this.application) {
        this.reset();

        this.primaryCustomer.address = primaryAddress;
        this.policies = [];
        if (policyToSwitchTo) {
          this.application.addPrimaryPolicy(policyToSwitchTo);
        } else {
          const policy = this.newPolicy();
          this.application.addPrimaryPolicy(policy);
        }
        this.updateInsuredEmailIfNeeded();

        this.application.primaryPolicy.product = (policyToSwitchTo && policyToSwitchTo.product) || product;
        this.application.primaryPolicy.selectedQuote = null;
      }

      this.application.primaryPolicy.setQuote(JSON.parse(JSON.stringify(selectedQuote)));
    }
  }

  updateInsuredEmailIfNeeded() {
    const insured: Applicant = this.application.primaryPolicy.insured;
    if (insured && insured.person && !insured.person.email) {
      insured.person.email = this.email;
    }
  }

  @remote({ on: 'server' })
  createPolicy(selectedQuote, quoteKey): Promise<void> {
    if (this.application && this.application.primaryPolicy && this.application.primaryPolicy.isSubmitting()) {
      throw new Error('Application in Progress - Cannot start a new one');
    }

    const createApp = () => {
      if (!this.application) {
        this.application = Application.new(this);
        return this.application.assignApplicationNumber();
      }
    };

    const createPol = () => {
      if (!this.application.primaryPolicy) {
        const policy = this.newPolicy();
        policy.product = quoteKey;
        this.application.addPrimaryPolicy(policy);
      }

      this.application.primaryPolicy.setQuote(JSON.parse(JSON.stringify(selectedQuote)));

      if (this.prefilledCustomerEmail) {
        this.application.primaryPolicy.insured.person.email = this.prefilledCustomerEmail;
      }
    };

    return Promise.resolve().then(createApp).then(createPol);
  }

  newPolicy(): Policy {
    return Policy.new(this);
  }

  @remote({ on: 'server' })
  async submitPolicy() {
    // Make sure this is not a duplicate submission
    const policy = this.application.primaryPolicy;
    if (policy.submittedAt && policy.policyNumber) {
      return Promise.resolve(true);
    }

    const isEmailValidated = await this.isEmailValidated();

    if (!isEmailValidated) {
      throw new Error('Internal Error - email not validated');
    }

    await policy.assignPolicyNumber();

    return this.startPolicyWorkflow();
  }

  // Debt: Necessary because Customer object owns policy submission.
  //       We should consolidate/move what owns policy submission (controller vs. policy, app, customer)
  protected async isEmailValidated(): Promise<boolean> {
    // Legacy validation
    let isEmailValidated = this.emailValidated;

    if (!isEmailValidated) {
      const isAuth0Enabled = await Auth0Utils.isAuth0Enabled();

      if (isAuth0Enabled) {
        const auth0Account: Auth0ResponseAccount = await Auth0Client.getAccountInfo(this.email);
        isEmailValidated = auth0Account.email_verified;
      }
    }

    return isEmailValidated;
  }

  @remote({ on: 'server' })
  startPolicyWorkflow() {
    // Update progress, submit the policy to workflow and motor on.
    this.advanceApplicationProgress(Assumptions.progress.ApplicationSubmitted);

    const policy = this.application.primaryPolicy;
    policy.submittedAt = new Date();
    policy.submittedFromIP = this.amorphic['incomingIP'];
    policy.maxTerm = policy.calculatedMaxTerm();
    LoggerSingleton.getInstance().info(
      {
        module: 'Customer',
        function: 'startPolicyWorkflow',

        category: Category.request,
      },
      `policy submited and starting workflow `
    );

    const startWorkflow = async () => {
      const firstStage = policy.isMMDirectPolicy() ? 'IB1 Check Underwriting Consent Requirements' : undefined;
      await this.application.primaryPolicy.startWorkflow(firstStage);

      LoggerSingleton.getInstance().info(
        {
          module: 'Customer',
          function: 'startPolicyWorkflow',
          category: Category.request,
          data: {
            policyNumber: this.application.primaryPolicy.policyNumber,
          },
        },
        `Workflow created for policy`
      );
    };
    return this.registerPostServerCallCallback(() => startWorkflow());
  }

  getSelectedQuote(showAll?) {
    const customers = [];
    let quote;

    if (this.primaryCustomer.selectedQuote && !this.primaryCustomer.policyHidden) {
      quote = JSON.parse(JSON.stringify(this.primaryCustomer.selectedQuote));
      quote.customer = this.primaryCustomer;
      customers.push(quote);
    } else {
      const quoteOpts = this.getQuoteOptions();

      if (showAll && !this.primaryCustomer.policyHidden && quoteOpts.face > 0) {
        customers.push({
          customer: this.primaryCustomer,
        });
      }
    }

    return this.quoteEngine.getSelectedQuote(customers);
  }

  // Overridden by HavenCustomer
  getDefaultSelectedQuote(): SelectedQuote {
    return null;
  }

  compute() {
    if (this.quoteEngine) {
      this.quoteEngine.compute();
    }
  }

  getSurvivor(deceased) {
    return deceased === this.primaryCustomer ? this.alternateCustomer : this.primaryCustomer;
  }

  remove() {
    return this.persistDelete();
  }

  getPrimaryCustomerGender() {
    if (this.doesPrimaryCustomerRequireUnisexRates()) {
      return 'unisex';
    } else {
      return this.primaryCustomer.gender;
    }
  }

  doesPrimaryCustomerRequireUnisexRates() {
    const state = this.primaryCustomer && this.primaryCustomer.address ? this.primaryCustomer.address.state : null;
    return !!MetadataHierarchy.getConfigurationByChannelAndState(this.channel, state, 'hasUnisexRates');
  }

  @remote({ on: 'server' })
  async createSMSPhone() {}

  // Overriden in CoverpathCustomer
  isCoverpath2019MetadataEnabled(policy?: Policy): boolean {
    return false;
  }

  // Overriden in CoverpathCustomer
  getCoverpathFilingType(policy?: Policy): string {
    return '';
  }

  isCoverpathCustomer(): boolean {
    return this.constructor.name === 'CoverpathCustomer';
  }

  isMMDirectCustomer() {
    return this.constructor.name === 'MMDirectCustomer';
  }

  getDocuSign() {
    return DocuSign;
  }
}
